import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import axios from "axios";
import { BilingualEyebrow, BilingualHeading } from "@/components/ui/bilingual-heading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fallbackPartners = [
  { id: "1", name: "CEVA Sant\u00e9 Animale", category: "Veterinary", website_url: "https://www.ceva.com" },
  { id: "2", name: "University of Graz", category: "Academic Research", website_url: "https://www.uni-graz.at" },
  { id: "3", name: "University of P\u00e9cs \u2014 National Laboratory of Virology", category: "BSL-4 Research Laboratory", website_url: "https://vnl.pte.hu/en" },
  { id: "4", name: "European Defence Fund", category: "Defense", website_url: "https://defence-industry-space.ec.europa.eu/eu-defence-industry/european-defence-fund-edf_en" },
  { id: "5", name: "Hungarian Defence Force", category: "Defense", website_url: "https://honvedseg.hu" },
  { id: "6", name: "Zatloukal Innovations", category: "Technology", website_url: "https://zatloukal-innovations.at/" },
];

// Authoritative logo/link data — ALWAYS overrides DB values
const knownLogos = [
  { match: ["CEVA", "Sante", "Santé"], logo_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/c3fkfx03_LOGO_CEVA_SANTE_ANIMALE.png", website_url: "https://www.ceva.com" },
  { match: ["Graz"], logo_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/fdhye2pd_unviersity%20of%20gratz%20logo%20new.jpg", website_url: "https://www.uni-graz.at" },
  { match: ["P\u00e9cs", "Virology", "VNL", "PTE"], logo_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/5avj3ocx_PTE_vnl_logo.png", website_url: "https://vnl.pte.hu/en" },
  { match: ["European", "EDF", "Defence Fund"], logo_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/mssmd791_EDF%20log.png", website_url: "https://defence-industry-space.ec.europa.eu/eu-defence-industry/european-defence-fund-edf_en" },
  { match: ["Hungarian", "Honv\u00e9d", "Defence Force"], logo_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/icshsytg_Logo_of_the_Hungarian_Defence_Forces.svg", website_url: "https://honvedseg.hu" },
  { match: ["Zatloukal"], logo_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/45g1whgo_Logo_Zatloukal_Innovations-Logo.svg", website_url: "https://zatloukal-innovations.at/" },
];

function enrichPartner(p) {
  if (!p.name) return p;
  for (const known of knownLogos) {
    if (known.match.some(m => p.name.includes(m))) {
      // FORCE override — never trust DB for logo/website of known partners
      return { ...p, logo_url: known.logo_url, website_url: known.website_url };
    }
  }
  return p;
}

export default function Partners() {
  const [partners, setPartners] = useState(() => fallbackPartners.map(enrichPartner));

  useEffect(() => {
    axios.get(`${API}/partners`).then(r => {
      if (r.data?.length) {
        // Filter out ThermoFisher if it still exists in production DB
        const filtered = r.data.filter(p => !p.name?.includes("ThermoFisher"));
        const enriched = filtered.map(enrichPartner);
        // If Graz is missing from API, add from fallback
        const hasGraz = enriched.some(p => p.name?.includes("Graz"));
        if (!hasGraz) {
          const grazFallback = fallbackPartners.find(p => p.name.includes("Graz"));
          if (grazFallback) enriched.push(enrichPartner(grazFallback));
        }
        setPartners(enriched);
      }
    }).catch(() => {});
  }, []);

  return (
    <section id="partners" data-testid="partners-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16 text-center">
          <BilingualEyebrow en="Partners &amp; Collaborators" hu="Partnerek &eacute;s egy&uuml;ttm&#369;k&ouml;d&#337;k" className="text-[#6B7280]" />
          <BilingualHeading en="Organizations We Have Worked With" hu="Szervezetek, akikkel egy&uuml;tt dolgoztunk" colorClass="text-[#111827]" className="max-w-3xl mx-auto" />
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
          {partners.map((p, i) => {
            const url = p.website_url || fallbackPartners.find(fp => p.name?.includes(fp.name?.split(' ')[0]))?.website_url;
            return (
              <motion.a
                key={p.id}
                href={url || "#"}
                target={url ? "_blank" : undefined}
                rel={url ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`partner-card-${i}`}
                className="flex flex-col items-center justify-center p-6 md:p-8 border border-[#E5E7EB] hover:border-[#EE1D25]/30 hover:shadow-lg transition-all duration-300 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#EE1D25]/30 focus:ring-offset-2"
                aria-label={`Visit ${p.name} website`}
              >
                {p.logo_url ? (
                  <div className="h-16 flex items-center justify-center mb-4">
                    <img
                      src={p.logo_url}
                      alt={`${p.name} logo`}
                      className="max-h-14 max-w-[140px] object-contain grayscale group-hover:grayscale-0 transition-all duration-500"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#F9FAFB] flex items-center justify-center mb-4 group-hover:bg-[#EE1D25]/5 transition-colors">
                    <span className="font-heading text-xl font-bold text-[#9CA3AF] group-hover:text-[#EE1D25] transition-colors">
                      {p.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                )}
                <p className="font-heading text-sm font-semibold text-[#111827] text-center group-hover:text-[#EE1D25] transition-colors">{p.name}</p>
                <p className="text-xs text-[#9CA3AF] mt-1">{p.category}</p>
                {url && <ExternalLink size={12} className="mt-2 text-[#D1D5DB] group-hover:text-[#EE1D25] transition-colors" />}
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
